import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fab, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, ListSubheader, Slide, Typography } from "@material-ui/core";
import { ArrowBack, Call, CloudDownload, Delete, Email, FileCopy, Map, Person, PictureAsPdf, Send } from "@material-ui/icons";
import { Subscribe } from "@react-rxjs/core";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { Link, Route, Switch, useHistory } from "react-router-dom";

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { FullscreenLoading } from "components/FullscreenLoading";
import { MenuFab } from "components/MenuFab";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation } from "components/PageNavigation";
import { analytics } from "FirebaseConfigs";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppUser } from "models/AppUser";
import { EnqueueSnacbar } from "models/EnqueueSnacbar";
import { DbDoc, removeRef } from "models/FbDoc";
import { Grant } from "models/Grant";
import { Nullable } from "models/Nullable";
import { Offer } from "models/Offer";
import { OfferMessage, OfferMessageType } from "models/OfferMessage";
import { getOrgUserGrants, noOrgUserNameText, OrgUser } from "models/OrgUser";
import { EnumPresentMap } from "models/PropertyMap";
import { Paths } from "Paths";
import { EmptyPage } from "screens/EmptyPage";
import { dateFormat } from "Util/date.utils";
import { getOfferPdf, OfferPdfViewer, pdfData$ } from "Util/generateOfferPdf";

import { CustomPriceFieldsList } from "./CreateOffer/OfferRoomCreationScreen";
import { OfferSummaryOverviewLinks, OfferSummarySites } from "./CreateOffer/OfferSummarySites";

type Props = {
    offerId: string
}

type DeleteModalProps = {
    onDelete: () => void
    onClose: () => void
}

export function DeleteModal({ onDelete, onClose }: DeleteModalProps) {
    return (
        <Dialog
            open
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">Slett anbud</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    Er du sikker på at du vil slette anbudet?
                    Dette kan ikke angres!
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">Avbryt</Button>
                <Button onClick={() => {
                    onDelete()
                }} color="primary" autoFocus>Ok</Button>
            </DialogActions>
        </Dialog>
    )
}

export const downloadFile = (file: string, filename: string) => {
    const a = document.createElement('a')
    a.setAttribute('href', file)
    a.setAttribute('download', filename + '.pdf')
    document.body.appendChild(a)
    a.click()
    a.parentNode?.removeChild(a)
}

export async function downloadOfferPDF(offer: DbDoc<Offer>) {
    const pdfData = await getOfferPdf(offer, false)
    await downloadFile(`data:application/pdf;base64,${pdfData}`, [
        'Anbud',
        'Adresse',
        offer.data.address?.addressString,
        'Tlf',
        offer.data.contactInfo?.phone?.phoneNumberString,
        'Navn',
        offer.data.contactInfo?.name?.fullName,
        'Epost',
        offer.data.contactInfo?.email?.email,
        'Id',
        offer.data.id,
    ].join('_'))
}

export function sendOfferEmail(offerId: string, orgId: string, appUser: AppUser, orgUser: OrgUser, enqueueSnackbar: EnqueueSnacbar, successMessage: Nullable<string>) {
    FirebaseUtils.createDocument<OfferMessage>(
        true,
        Collections.OFFER_MESSAGES,
        null,
        appUser,
        orgUser,
        AnalyticsEvent.SENT_OFFER,
        AnalyticsEvent.SENT_OFFER,
        AnalyticsEvent.SEND_OFFER_ERROR,
        enqueueSnackbar,
        successMessage,
        {
            type: OfferMessageType.SEND_OFFER,
            includePdf: true,
            text: null,
            offer: {
                id: offerId,
            },
            org: {
                id: orgId,
            },
        },
        (doc) => ({
            offerId: doc.id,
        }),
    )
}

export function createdByItem(orgUser?: Nullable<OrgUser>, gotoAppUser?: boolean) {
    return orgUser ? (
        <ListItem component={Link} to={gotoAppUser ? (Paths.APP_USERS + '/' + orgUser?.userId) : (Paths.ORG_USERS + '/' + orgUser?.id)}>
            <ListItemAvatar>
                <Avatar src={orgUser?.avatarImage?.original.url || undefined}>
                    <Person />
                </Avatar>
            </ListItemAvatar>
            <ListItemText primary="Opprettet av" secondary={noOrgUserNameText(orgUser?.orgContactInfo?.name?.fullName)} />
            <ListItemSecondaryAction>
                {orgUser?.orgContactInfo?.phone ? (
                    <IconButton
                        component="a"
                        href={`tel:${orgUser?.orgContactInfo?.phone?.phoneNumberString}`}>
                        <Call />
                    </IconButton>
                ) : null}
                {orgUser?.orgContactInfo?.email ? (
                    <IconButton
                        component="a"
                        href={`mailto:${orgUser?.orgContactInfo?.email?.email}`}>
                        <Email />
                    </IconButton>
                ) : null}
            </ListItemSecondaryAction>
        </ListItem>
    ) : null
}


export function createdAtItem(listScreen: Paths, createdAt?: Date) {
    return createdAt ? (
        <ListItem component={Link} to={`${listScreen}/?s=` + encodeURI(dateFormat.format(createdAt))}>
            <ListItemText primary="Opprettet" secondary={dateFormat.format(createdAt)} />
        </ListItem>
    ) : null
}

type OfferActionsProps = {
    actions: Nullable<[Grant[], number, JSX.Element, string, (() => void), { disabled?: boolean, color?: 'primary' | 'secondary', speedDialOptions?: [string, JSX.Element, () => void][] }?]>[]
    orgUserGrants: EnumPresentMap<typeof Grant>
}

export function OfferActions({ actions, orgUserGrants }: OfferActionsProps) {
    const accessibleActions = actions.filter((action) => {
        if (!action) {
            return false
        }
        const [grants] = action
        return grants.every(grant => orgUserGrants[grant])
    })

    return accessibleActions.length ? (
        <>
            <ListSubheader>Handlinger</ListSubheader>
            <Box paddingY={2} maxWidth="xs" mb={4}>
                <Grid
                    //component={Paper}
                    container
                    direction="row"
                    justify="space-evenly"
                    alignItems="center"
                >
                    {accessibleActions.map((action, index) => {
                        if (!action) {
                            return null
                        }
                        const [, order, icon, label, onClick, options] = action
                        return [
                            <Slide key={label} direction={index < 2 ? 'right' : 'left'} in mountOnEnter unmountOnExit timeout={100 + ((order) * 100)}>
                                <Grid item key={icon.key} style={{ position: 'relative' }}>
                                    {
                                        options?.speedDialOptions ? (
                                            <MenuFab
                                                color={options?.color || "secondary"}
                                                label={label}
                                                icon={icon}
                                                options={options.speedDialOptions}
                                            />
                                        ) : (

                                            <Fab
                                                color={options?.color || "secondary"}
                                                aria-label={label}
                                                onClick={onClick}
                                                disabled={options?.disabled}
                                            >
                                                {icon}
                                            </Fab>
                                        )}
                                    <Typography noWrap variant="subtitle2" style={{ position: 'absolute', bottom: -30, left: '50%', transform: 'translateX(-50%)' }}>{label}</Typography>
                                </Grid>
                            </Slide>
                        ]
                    })}
                </Grid>
            </Box>
        </>
    ) : null
}

export function OfferScreen({ offerId }: Props) {
    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory();
    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const [appUserDoc, loadingAppUser] = useAppUser()

    const [offerDoc, loadingOffer] = FirebaseUtils.useDocumentData<Offer>(
        false,
        Collections.OFFERS,
        offerId,
        AnalyticsEvent.GET_OFFER_ERROR
    )

    const orgUser = orgUserDoc?.data || null
    const appUser = appUserDoc?.data || null
    const offer = offerDoc?.data || null

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const orgUserGrants = getOrgUserGrants(orgUser)

    const path = '/anbud/' + offerId;

    const loading = loadingOrgUser || loadingOffer || loadingAppUser

    if (loading) {
        return <FullscreenLoading screenId="Offer" />
    }

    if (!offerDoc || !offer) {
        return <EmptyPage text={`Anbudet finnes ikke`} />
    }

    const { id, address } = offer;

    const deleteModal = deleteModalOpen ? (
        <DeleteModal
            onClose={() => setDeleteModalOpen(false)}
            onDelete={() => {
                FirebaseUtils.deleteDocument(
                    true,
                    Collections.OFFERS,
                    offerId,
                    appUser,
                    orgUser,
                    AnalyticsEvent.DELETED_OFFER,
                    AnalyticsEvent.DELETED_OFFER,
                    AnalyticsEvent.DELETE_OFFER_ERROR,
                    enqueueSnackbar,
                    'Slettet anbud',
                    () => ({ offerId, }),
                )
                history.push(Paths.OFFER)
            }}
        />
    ) : null

    return (
        <Switch>
            <Route exact path={path}>
                <PageNavigation
                    title="Anbud"
                    backButton={
                        <IconButton color="inherit" component={Link} to="/anbud">
                            <ArrowBack />
                        </IconButton>
                    }
                >
                    {deleteModal}
                    <List dense>
                        {createdByItem(offer.offerCreator.orgUser)}
                        {createdAtItem(Paths.OFFER, offerDoc.metadata.createdAt.toDate())}
                        <ListItem component={Link} to={'/anbud/?s=' + encodeURI(address?.addressString || '')}>
                            <ListItemText primary="Adresse" secondary={address?.addressString} />
                            <ListItemSecondaryAction>
                                <IconButton
                                    component="a"
                                    target="_blank"
                                    href={`http://maps.google.com/?q=${address?.addressString}`}>
                                    <Map />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                        {offer.comment ? (
                            <ListItem>
                                <ListItemText primary="Kommentar" style={{ whiteSpace: 'pre-wrap' }} secondary={offer.comment} />
                            </ListItem>
                        ) : null}
                        <ListItem>
                            <ListItemText primary="Pris" secondary={`${offer.price.subTotal},-`} />
                        </ListItem>
                        {offer.price.discount ? (
                            <ListItem>
                                <ListItemText primary="Avslag" secondary={`${offer.price.discount},-`} />
                            </ListItem>
                        ) : null}
                        {offer.price.manualPrice !== null ? (
                            <ListItem>
                                <ListItemText primary="Manuell Pris" secondary={`${offer.price.manualPrice},-`} />
                            </ListItem>
                        ) : null}
                    </List>
                    <OfferActions
                        orgUserGrants={orgUserGrants}
                        actions={[
                            (appUser && orgUser) ? [[], 2, <FileCopy />, 'Kopier', () => {
                                const { id, ...rest } = offer
                                const newId = FirebaseUtils.createDocument<Offer>(
                                    true,
                                    Collections.OFFER_DRAFTS,
                                    null,
                                    appUser,
                                    orgUser,
                                    AnalyticsEvent.COPIED_OFFER,
                                    AnalyticsEvent.CREATED_OFFER_DRAFT,
                                    AnalyticsEvent.CREATE_OFFER_DRAFT_ERROR,
                                    enqueueSnackbar,
                                    'Anbud kopiert',
                                    rest,
                                    (doc) => ({
                                        offerDraftId: doc.id,
                                    }),
                                )
                                history.push(Paths.OFFER_DRAFTS + '/' + newId)
                            }] : null,
                            [[Grant.DOWNLOAD_OFFER], 1, <PictureAsPdf />, 'PDF', () => {
                                analytics.logEvent(AnalyticsEvent.DOWNLOADED_OFFER_PDF, {
                                    offerId,
                                    userId: orgUser?.userId,
                                    orgId: orgUser?.org.id,
                                    orgUserId: orgUser?.id,
                                })
                            }, {
                                speedDialOptions: [
                                    ['Last Ned PDF', <CloudDownload />, () => {
                                        downloadOfferPDF(removeRef(offerDoc))
                                        analytics.logEvent(AnalyticsEvent.VIEWED_OFFER_PDF, {
                                            offerId,
                                            userId: orgUser?.userId,
                                            orgId: orgUser?.org.id,
                                            orgUserId: orgUser?.id,
                                        })
                                    }
                                    ],
                                    ['Se PDF', <PictureAsPdf />, () => { history.push(path + '/pdf') }],
                                ]
                            }],
                            (appUser && orgUser) ? [[Grant.SEND_OFFER], 1, <Send />, 'Send', () => {
                                sendOfferEmail(offer.id, offer.org.id, appUser, orgUser, enqueueSnackbar, 'Anbud sendt')
                            }] : null,
                            [[Grant.DELETE_OFFER], 2, <Delete />, 'Slett', () => setDeleteModalOpen(true)],
                        ]}
                    />
                    <OfferSummaryOverviewLinks path={path} />
                    <List dense>
                        {offer.customPriceFields && <CustomPriceFieldsList customPriceFields={offer.customPriceFields} />}
                    </List>
                    <Box my={5} />
                </PageNavigation >
            </Route>
            <Route path={path + '/pdf'} render={() => {
                return (
                    <PageNavigation title="PDF" backHistoryButton>
                        <Subscribe source$={pdfData$}>
                            <OfferPdfViewer offer={removeRef(offerDoc)} scale={2} />
                        </Subscribe>
                    </PageNavigation>
                )
            }} />
            <OfferSummarySites
                offerId={id}
                path={path}
                offerRooms={offer.offerRooms}
                offerRoomNames={offer.offerRoomNames}
                offerRoomComments={offer.offerRoomComments}
                discount={offer.price.discount}
                manualPrice={offer.price.manualPrice}
                hideUnitPrices={offer.hideUnitPrices}
                contactInfo={offer.contactInfo}
            />
        </Switch >
    )
}